/** Wrapper around the LSK POS Web Extensions API, with a dev mock.
 *
 * The native host injects `posContext` + `pos_*` globals into the web view. We feature-detect
 * them; when absent (normal browser / tests) we return a fully working MOCK so the app runs in
 * dev. The async `pos_*` calls use a `callback({ error, data })` pattern, promisified by `call`.
 */
import type {
  PosAccount,
  PosAccountLine,
  PosCallback,
  PosConsumer,
  PosContext,
  PosDiscount,
  PosExternalReference,
  RawPosAccount,
  RawPosTransactionLine,
} from './types'

/** Parse one external-reference entry into `{ prefix, reference }`.
 *
 * `pos_getCurrentAccount` returns external references as `"PREFIX:reference"` STRINGS (verified
 * 2026-06-16 device dump). The reference itself may contain a colon (e.g. `rewards:<id>`), so we
 * split on the FIRST colon only. The `{ prefix, reference }` object form (what the webhook uses) is
 * also tolerated, in case a host/version differs. */
export function parseExternalRef(entry: string | PosExternalReference): PosExternalReference | null {
  if (typeof entry === 'string') {
    const idx = entry.indexOf(':')
    return idx >= 0 ? { prefix: entry.slice(0, idx), reference: entry.slice(idx + 1) } : { reference: entry }
  }
  if (entry && typeof entry === 'object' && typeof entry.reference === 'string') {
    return { prefix: entry.prefix, reference: entry.reference }
  }
  return null
}

/** Is this line still really on the ticket?
 *
 * Voiding a line does NOT take it out of `transactionLines` — it stays with `quantity: 0` (device
 * dump 2026-07-29). Left in, it shows up as a ghost the cashier is invited to discount: the reward
 * picker offered three items for a two-item ticket, one of them priced 0.00.
 *
 * Keyed on QUANTITY, never on price. A line we already discounted with BADGER_FREE is also
 * `amount: 0` while being perfectly live (`quantity: 100`, `unitAmount: 650`), so filtering on
 * amount would hide exactly the rewards the cashier most needs to see. */
export function isLiveLine(line: RawPosTransactionLine): boolean {
  return (line.quantity ?? 100) > 0
}

/** Map the raw host account (verified 2026-06-09 device schema) to our normalized shape, so the
 * rest of the app works with stable field names instead of LSK's `transactionLines`/`amount`/etc.
 * Voided lines are dropped here, so nothing downstream has to know they linger. */
function normalizeAccount(raw: RawPosAccount): PosAccount {
  // Merge the live + `latest` external-reference arrays (deduped), parsing the device's string form
  // into objects — either may carry the BADGER tags from a previous open of this same sale.
  const refs: PosExternalReference[] = []
  const seen = new Set<string>()
  for (const entry of [...(raw.externalReferences ?? []), ...(raw.latestExternalReferences ?? [])]) {
    const ref = parseExternalRef(entry)
    if (!ref?.reference) continue
    const key = `${ref.prefix ?? ''}:${ref.reference}`
    if (!seen.has(key)) {
      seen.add(key)
      refs.push(ref)
    }
  }
  // Filter and map in ONE pass, matching the loop above rather than chaining `.filter().map()`.
  const lines: PosAccountLine[] = []
  for (const l of raw.transactionLines ?? []) {
    if (!isLiveLine(l)) continue
    lines.push({
      lineId: l.identifier,
      itemId: l.itemIdentifier,
      itemSku: l.itemSku,
      name: l.text,
      priceCents: l.amount,
      quantity: (l.quantity ?? 100) / 100,
      discounts: l.discounts,
    })
  }
  return { number: raw.identifier, lines, externalReferences: refs }
}

/** Reference code of the manually-created 100% per-item discount used for redemptions.
 * Must match the discount configured on the device (verified by the backend health check, DEV-263). */
export const BADGER_FREE_DISCOUNT_CODE = 'BADGER_FREE'

/** Prefix used when tagging a sale via pos_addExternalReference, so the order-closed webhook
 * can correlate the Lightspeed order back to a Badger pending transaction. */
export const BADGER_REFERENCE_PREFIX = 'BADGER'

/** Value marker for the rewards external reference: `rewards:` followed by a comma-separated
 * list of redeemed reward (completed-card) ids, tagged under BADGER_REFERENCE_PREFIX. The
 * webhook runner claims one or many cards from this single reference. The consumer reference
 * (a Firebase uid, no `:`) is never confused with it. */
export const BADGER_REWARDS_MARKER = 'rewards:'

/** One redeemed reward and where its BADGER_FREE discount landed. `targetToken` is the line id the
 * discount sits on, or the literal `account` for a whole-sale discount (the encoded form of
 * `RedeemTarget`). It rides the external reference as `<rewardId>@<targetToken>` so the extension
 * can rebuild the cancel (Annuler) state after a reopen; the backend ignores the suffix. */
export interface RedeemedRewardEntry {
  rewardId: string
  /** Encoded discount target (line id or `account`). Omitted for a legacy ref with no target. */
  targetToken?: string
}

/** Build the rewards external-reference value from the redeemed rewards (deduped by reward id,
 * last target wins). Each entry is encoded `<rewardId>@<targetToken>` (or just `<rewardId>` when
 * the target is unknown). The extension re-tags the COMPLETE current set on every apply/cancel, so
 * the latest reference is authoritative — see `badgerRedeemedRewardIds` (DEV-304). */
export function badgerRewardsReference(entries: RedeemedRewardEntry[]): string {
  const byId = new Map<string, string | undefined>()
  for (const entry of entries) {
    const id = entry.rewardId?.trim()
    if (id) byId.set(id, entry.targetToken?.trim() || undefined)
  }
  const parts = [...byId].map(([id, token]) => (token ? `${id}@${token}` : id))
  return BADGER_REWARDS_MARKER + parts.join(',')
}

/** The `<rewardId>[@<targetToken>]` tokens from the authoritative `rewards:` reference (LATEST-WINS).
 *
 * The extension re-tags the complete reward set on every apply/cancel, so only the MOST RECENTLY
 * tagged `rewards:` reference counts — unioning every reference would resurrect a reward the cashier
 * just cancelled (DEV-304). "Most recent" = the last BADGER `rewards:` entry in
 * `account.externalReferences` (which `normalizeAccount` builds as live-then-`latest`, deduped).
 * ⚠ ordering unverified on device (DEV-67); mirrors the backend `claim_parsing.reward_ids_from_refs`. */
function latestRewardsTokens(account: PosAccount): RedeemedRewardEntry[] {
  let latest: string | null = null
  for (const ref of account.externalReferences ?? []) {
    if (ref?.prefix !== BADGER_REFERENCE_PREFIX) continue
    const value = ref.reference?.trim()
    if (value && value.startsWith(BADGER_REWARDS_MARKER)) latest = value
  }
  if (!latest) return []
  const out: RedeemedRewardEntry[] = []
  for (const token of latest.slice(BADGER_REWARDS_MARKER.length).split(',')) {
    const [rawId, rawTarget] = token.split('@')
    const rewardId = rawId?.trim()
    if (rewardId) out.push({ rewardId, targetToken: rawTarget?.trim() || undefined })
  }
  return out
}

/** The reward (completed-card) ids already redeemed on this open sale, read back from the
 * authoritative `rewards:` reference (latest-wins, `@target` suffix stripped). Reading them on a
 * fresh open keeps a redeemed reward shown as applied (and cancelable) across closes — it can't be
 * applied twice. De-duplicated, order-preserving. */
export function badgerRedeemedRewardIds(account: PosAccount): string[] {
  const ids = new Set<string>()
  for (const { rewardId } of latestRewardsTokens(account)) ids.add(rewardId)
  return [...ids]
}

/** Where each redeemed reward's discount landed, recovered from the `rewards:` reference, so the
 * Annuler button works after a reopen. Maps reward id → target token (line id, or `account`). Ids
 * tagged by an older extension without a target are simply absent. */
export function badgerAppliedTargetTokens(account: PosAccount): Record<string, string> {
  const out: Record<string, string> = {}
  for (const { rewardId, targetToken } of latestRewardsTokens(account)) {
    if (targetToken) out[rewardId] = targetToken
  }
  return out
}

/** The Badger consumer uid already linked to this account, or null. We tag the consumer uid as a
 * BADGER external reference on scan; reading it back on a fresh open lets us re-identify the same
 * customer without a second scan. The rewards reference (`rewards:<csv>`) shares the prefix, so we
 * skip anything starting with the rewards marker — mirrors the backend `consumer_uid_from_refs`. */
export function badgerConsumerReference(account: PosAccount): string | null {
  for (const ref of account.externalReferences ?? []) {
    if (ref?.prefix !== BADGER_REFERENCE_PREFIX) continue
    const value = ref.reference?.trim()
    if (value && !value.startsWith(BADGER_REWARDS_MARKER)) return value
  }
  return null
}

export interface LightspeedPosApi {
  /** Static business/location/device/user context, or null in dev. */
  getContext(): PosContext | null
  getCurrentAccount(): Promise<PosAccount>
  getAvailableDiscounts(): Promise<PosDiscount[]>
  /** Toggle a discount (e.g. BADGER_FREE) on a specific account line. */
  toggleLineDiscount(discountCode: string, lineId: string): Promise<void>
  /** Toggle a global discount (e.g. BADGER_FREE) on the whole current account. */
  toggleGlobalDiscount(discountCode: string): Promise<void>
  /** Tag the sale so the backend webhook can correlate it. Fire-and-forget. */
  addExternalReference(reference: string, prefix?: string): void
  /** Ring an item up on the current account.
   *
   * Fire-and-forget by design — the host function takes no callback, so this CANNOT report
   * success. The caller must re-read the account to see whether a line appeared
   * (`addItemAndFindLine` does that). There is no remove counterpart in the documented API: an
   * item added here can only be voided by the cashier on the till. */
  addItem(itemId: string): void
  /** Link the current sale to a Badger consumer. Fire-and-forget. */
  setConsumer(consumer: PosConsumer): void
  /** Persist a string in the POS app's defaults. Overwrites any previous value for `key`. */
  storeData(key: string, value: string): void
  /** Read a value written by `storeData`.
   *
   * `unknown`, not `string`: the device (2026-07-29) hands back an already-PARSED OBJECT even
   * though `storeData` takes a string. Typing it as string made `JSON.parse` stringify it to
   * "[object Object]" and throw, so every restore silently came back empty. Callers must handle
   * both shapes. Null/undefined when the key was never written. */
  loadData(key: string): Promise<unknown>
  /** Subscribe to card scans (barcode + NFC). Returns an unsubscribe function. */
  onCardScan(handler: (cardId: string) => void): () => void
  /** Close the web view. */
  close(): void
}

/** Ring an item up and return the line the POS created for it, or null if none appeared.
 *
 * `pos_addItemToCurrentAccount` takes NO callback — there is no completion signal and no error
 * channel, so "did it work?" can only be answered by re-reading the account and looking for a line
 * that was not there before. The host inserts asynchronously, hence the brief poll rather than a
 * single read.
 *
 * Matched by line id (not by SKU alone) so that ringing up a product the ticket ALREADY holds
 * returns the new line rather than the existing one — otherwise a reward would discount the drink
 * the customer is paying for and leave the free one at full price. */
/** The `pos_*` functions this host ACTUALLY injected.
 *
 * `globals.d.ts` is only what we declared, so it cannot answer "does this POS support X?" — and
 * guessing from it once shipped a wrong "the POS does not allow it" message. This asks the runtime.
 * Reported with add attempts so a failure says whether the function is even present. */
export function hostFunctionNames(): string[] {
  try {
    return Object.getOwnPropertyNames(globalThis)
      .filter((k) => k.startsWith('pos_'))
      .sort()
  } catch {
    return []
  }
}

/** What an add attempt did, so a silent failure can be diagnosed without another device round-trip. */
export interface AddItemReport {
  line: PosAccountLine | null
  /** Ids passed to the host, in order. */
  tried: string[]
  /** Whether the host injected the add function at all. */
  hasAddItem: boolean
  /** Whether calling it threw, and with what. */
  threw?: string
  linesBefore: number
  linesAfter: number
}

export async function addItemAndFindLine(
  api: LightspeedPosApi,
  candidateIds: string[],
  { attempts = 6, delayMs = 100 }: { attempts?: number; delayMs?: number } = {},
): Promise<AddItemReport> {
  // TRIES EACH CANDIDATE because the documented signature is just `itemId` ("Reference identifier
  // used to identify the item to add") and does not say which of the two ids a line carries that
  // means. The docs use the same phrase for `pos_toggleDiscount`'s `lineId`, which is the internal
  // `identifier` — so the internal `itemIdentifier` is the better first guess, with the SKU as
  // fallback. With no error channel, asking the account is the only way to find out, and trying
  // both costs one wasted poll instead of another trip to a device.
  const report: AddItemReport = {
    line: null,
    tried: [],
    hasAddItem: hostFunctionNames().includes('pos_addItemToCurrentAccount'),
    linesBefore: ((await api.getCurrentAccount()).lines ?? []).length,
    linesAfter: 0,
  }
  for (const candidate of candidateIds) {
    const before = new Set(((await api.getCurrentAccount()).lines ?? []).map((l) => l.lineId))
    report.tried.push(candidate)
    try {
      api.addItem(candidate)
    } catch (e) {
      // A host that never injected the function throws ReferenceError here. Recorded rather than
      // thrown, so the remaining candidate still gets its turn and the report reaches the caller.
      report.threw = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
    }
    for (let i = 0; i < attempts; i++) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
      const lines = (await api.getCurrentAccount()).lines ?? []
      report.linesAfter = lines.length
      const fresh = lines.filter((l) => !before.has(l.lineId))
      // Prefer a new line that matches what we asked for; fall back to a lone new line, since the
      // host may key it by the other id than the one we sent.
      const added = fresh.find((l) => l.itemSku === candidate || l.itemId === candidate)
      if (added || fresh.length === 1) {
        report.line = added ?? fresh[0]
        return report
      }
    }
  }
  return report
}

/** One reward claimed on the open sale, and enough to undo it. */
export interface StoredClaim {
  rewardId: string
  /** The line its discount landed on, so cancel can toggle the same one back off. */
  lineId: string
  /** True when WE rang the product up — undo has to tell the cashier to void it, since the
   * documented API adds lines but never removes them. */
  added?: boolean
}

/** ⚠ STOPGAP. The durable home for claim state is the BACKEND pending doc, not the device.
 *
 * Why not an external reference: references can only be APPENDED — no edit, no delete — so
 * cancelling a reward would mean writing a fresh reference and trusting readers to take the last
 * one, and that "latest wins" ordering has never been verified on a device (DEV-67). An ordering
 * surprise there resurrects a reward the cashier just cancelled and gives away a second free item.
 *
 * Why not this either, long-term: `pos_storeData` writes to the POS APP'S DEFAULTS — device-wide
 * and outliving every sale. It does not follow the customer to another register, and the backend
 * cannot see it, which matters because the backend is what commits the claim when the order-closed
 * webhook confirms payment. `POST /pending` + `GET /pending` are the real answer (already live for
 * the M2 stamp/reward model, and mutable — dev-304 cancels through them); the points model just
 * has no shape there yet (TechSpec B2/B3).
 *
 * So: ONE key, holding the sale it belongs to. Anything belonging to a different sale reads as
 * empty, which keeps a skipped transaction from resurfacing as stale claims on the next ticket and
 * stops the defaults growing a key per sale forever. */
const CLAIM_STORE_KEY = 'badger_claims'

interface ClaimStoreValue {
  account?: string
  claims?: unknown
}

/** What a restore attempt saw, so a silent "came back empty" can be diagnosed. */
export interface LoadClaimsReport {
  claims: StoredClaim[]
  /** Whether anything was stored at all. */
  hadRaw: boolean
  /** The sale the stored state belonged to, vs the one we are opening onto. */
  storedAccount?: string
  currentAccount?: string
  /** Set when the store never answered — the host functions exist but take a callback, and a
   * callback that is never invoked would otherwise hang the restore forever. */
  timedOut?: boolean
  /** What the host actually handed back ('string' on a browser, 'object' on the device). */
  rawType?: string
  error?: string
}

export async function loadClaimsReport(
  api: LightspeedPosApi,
  accountNumber?: string,
): Promise<LoadClaimsReport> {
  const report: LoadClaimsReport = { claims: [], hadRaw: false, currentAccount: accountNumber }
  let raw: unknown = null
  try {
    // Raced against a timeout: `pos_loadData` is callback-style, and a host that simply never calls
    // back would leave this promise pending and the panel silently unrestored, forever.
    raw = await Promise.race([
      api.loadData(CLAIM_STORE_KEY),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000)).then(() => {
        report.timedOut = true
        return null
      }),
    ])
  } catch (e) {
    report.error = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
    return report
  }
  if (!raw) return report
  report.hadRaw = true
  report.rawType = typeof raw
  try {
    // The device returns an OBJECT here, a browser/mock returns the string we wrote. Parsing only
    // the string case is what broke this: `JSON.parse` coerced the object to "[object Object]" and
    // threw, so a perfectly good saved state was discarded on every single open.
    const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ClaimStoreValue
    report.storedAccount = parsed?.account
    if (!parsed?.account || parsed.account !== accountNumber) return report
    if (!Array.isArray(parsed.claims)) return report
    report.claims = parsed.claims.filter(
      (c): c is StoredClaim =>
        !!c && typeof c.rewardId === 'string' && typeof c.lineId === 'string',
    )
  } catch (e) {
    report.error = e instanceof Error ? `${e.name}: ${e.message}` : String(e)
  }
  return report
}

export async function loadClaims(api: LightspeedPosApi, accountNumber?: string): Promise<StoredClaim[]> {
  try {
    const raw = await api.loadData(CLAIM_STORE_KEY)
    if (!raw) return []
    const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ClaimStoreValue
    // Belongs to a different (older) sale → not ours. This is the staleness guard.
    if (!parsed?.account || parsed.account !== accountNumber) return []
    if (!Array.isArray(parsed.claims)) return []
    // Filter rather than trust: this string outlives app restarts and older builds, and a malformed
    // entry must not take points off a customer who never claimed anything.
    return parsed.claims.filter(
      (c): c is StoredClaim =>
        !!c && typeof c.rewardId === 'string' && typeof c.lineId === 'string',
    )
  } catch {
    return []
  }
}

/** Ceiling on stored claims. A sale cannot legitimately exceed the catalogue size, so this is
 * belt-and-braces — but it writes into the POS APP'S OWN DEFAULTS, which we share with the register
 * and never get to clean up, so a runaway here would become someone else's problem to discover on a
 * device we cannot inspect. Cheap to bound, expensive to debug. */
const MAX_STORED_CLAIMS = 50

export function saveClaims(
  api: LightspeedPosApi,
  accountNumber: string | undefined,
  claims: StoredClaim[],
): void {
  // ONE key, overwritten every time — never appended to, and never keyed per sale, so the defaults
  // hold at most this single small record no matter how many tickets the register rings up.
  api.storeData(
    CLAIM_STORE_KEY,
    JSON.stringify({ account: accountNumber, claims: claims.slice(0, MAX_STORED_CLAIMS) }),
  )
}

/** Promisify a `pos_*` callback-style call. */
function call<T>(invoke: (callback: PosCallback<T>) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    invoke((response) => {
      if (response?.error) reject(new Error(response.error.message))
      else resolve(response?.data as T)
    })
  })
}

function hasHost(): boolean {
  return typeof posContext !== 'undefined' && typeof pos_getCurrentAccount === 'function'
}

/** True only inside the real LSK POS web view — NOT a plain browser or the dev mock.
 * Use this (not `getContext()`, which the mock fills in) to gate POS-only behaviour like
 * auto-closing the extension. */
export function isPosHost(): boolean {
  return hasHost()
}

function createHostApi(): LightspeedPosApi {
  return {
    getContext: () => (typeof posContext !== 'undefined' ? posContext : null),
    getCurrentAccount: async () =>
      normalizeAccount(await call<RawPosAccount>((cb) => pos_getCurrentAccount(cb))),
    getAvailableDiscounts: () => call<PosDiscount[]>((cb) => pos_getAvailableDiscounts(cb)),
    toggleLineDiscount: async (code, lineId) => {
      await call((cb) => pos_toggleDiscount(code, lineId, cb))
    },
    toggleGlobalDiscount: async (code) => {
      await call((cb) => pos_toggleGlobalDiscount(code, cb))
    },
    addExternalReference: (reference, prefix = BADGER_REFERENCE_PREFIX) =>
      pos_addExternalReference(reference, prefix),
    addItem: (itemId) => pos_addItemToCurrentAccount(itemId),
    setConsumer: (consumer) => pos_setConsumer(consumer),
    storeData: (key, value) => pos_storeData(key, value),
    loadData: (key) => call<string | null>((cb) => pos_loadData(key, cb)),
    onCardScan: (handler) => {
      let active = true
      const cb: PosCallback<string> = (r) => {
        if (active && r?.data) handler(r.data)
      }
      pos_onBarcodeScanEvent(cb)
      pos_onNFCEvent(cb)
      return () => {
        active = false
      }
    },
    close: () => pos_close(),
  }
}

function createMockApi(): LightspeedPosApi {
  // Demo basket + discount list. This is the ONLY place either is hardcoded: the screens read
  // both through `getCurrentAccount()` / `getAvailableDiscounts()`, so on a real register they
  // are live POS data and nothing above this file changes.
  const account: PosAccount = {
    number: 'dev-account-0001',
    // Item ids match `FIXTURE_MENU` in `screens/marketplace-fixtures.ts`, and WHICH products are
    // on this ticket is what decides each reward's claim path. Pan de Yuca and Carbonara are
    // absent on purpose (the "add it to the order" case); change one file without the other and
    // the demo stops covering all four paths.
    // `itemSku` values are the merchant's real SKUs from the trial back office, matching
    // `FIXTURE_MENU` in `screens/marketplace-fixtures.ts` — that is what rewards match on. The
    // long `itemId`s alongside them are the shape of the real internal `itemIdentifier` (device
    // dump 2026-07-29): the two differ, and keeping BOTH here is what stops the mock from
    // agreeing with a match that would fail on the iPad. Which products are on this ticket is
    // what decides each reward's claim path; change one file without the other and the demo
    // stops covering all four. Pan de yuca and Carbonara are absent on purpose.
    lines: [
      // Two SEPARATE Cappuccino lines, not one line of quantity 2 — that is what a till looks
      // like when two people order the same drink, and it is what makes the "which line?" prompt
      // reachable in the demo.
      { lineId: 'l1', itemId: '1399154316148985', itemSku: '13', name: 'Cappuccino', priceCents: 650, quantity: 1 },
      { lineId: 'l2', itemId: '1399154316148985', itemSku: '13', name: 'Cappuccino', priceCents: 650, quantity: 1 },
      { lineId: 'l3', itemId: '1399154316148922', itemSku: '4', name: 'Mozza tomato', priceCents: 1200, quantity: 1 },
      { lineId: 'l4', itemId: '1399154316148927', itemSku: '5', name: 'Cheeseburger', priceCents: 1400, quantity: 1 },
      { lineId: 'l5', itemId: '1399154316148912', itemSku: '2', name: 'Blonde', priceCents: 700, quantity: 1 },
    ],
  }
  // Items the demo can RING UP but that start off the ticket. Each carries BOTH ids, because the
  // add call is given the internal identifier first and the SKU only as a fallback — a mock keyed
  // on one of them would invent a line with the wrong `itemSku`, and then reward matching would
  // "work" here against data no register would ever produce.
  // Every id here is REAL, read from device dumps (2026-07-29). Earlier these were invented, which
  // let the browser demo ring up products the iPad cannot — the mock agreed with a call that would
  // fail. Items whose internal id nobody has seen are deliberately ABSENT, so the mock refuses
  // exactly what the device refuses.
  const MOCK_MENU = [
    { sku: '1', internalId: '1399154316148907', name: 'Bière blanche', priceCents: 850 },
    { sku: '2', internalId: '1399154316148912', name: 'Blonde', priceCents: 700 },
    { sku: '3', internalId: '1399154316148917', name: 'Whiskey', priceCents: 1600 },
    { sku: '4', internalId: '1399154316148922', name: 'Mozza tomato', priceCents: 1200 },
    { sku: '5', internalId: '1399154316148927', name: 'Cheeseburger', priceCents: 1400 },
    { sku: '6', internalId: '1399154316148942', name: 'Embuscade', priceCents: 900 },
    { sku: '7', internalId: '1399154316148951', name: 'Tacos Lyonnais', priceCents: 950 },
    { sku: '12', internalId: '1399154316148981', name: 'Carbonara Maison', priceCents: 2500 },
    { sku: '13', internalId: '1399154316148985', name: 'Cappuccino', priceCents: 650 },
    { sku: '14', internalId: '1399154316148989', name: 'Pan de yuca', priceCents: 500 },
  ]
  const store: Record<string, string> = {}
  const log = (msg: string) => console.info(`[mock LS] ${msg}`)
  return {
    getContext: () => ({
      businessId: 'dev-business',
      businessName: 'Dev Cafe',
      locationId: 'dev-location',
      locationName: 'Dev Cafe — Main',
      deviceName: 'Dev iPad',
      deviceId: 'dev-device',
      userName: 'Dev Cashier',
      userId: 'dev-user',
      configurationName: 'Dev fixed POS',
      theme: { darkMode: false },
    }),
    getCurrentAccount: async () => structuredClone(account),
    // More than one code on purpose: a reward maps to whatever discount the merchant configured,
    // not necessarily the 100% BADGER_FREE one (Figma 2260:369 shows −50% and −10% codes).
    getAvailableDiscounts: async () => [
      { code: BADGER_FREE_DISCOUNT_CODE, name: 'Article offert · −100%' },
      { code: 'BADGER_50', name: 'Remise −50%' },
      { code: 'STAFF_10', name: 'Remise −10%' },
    ],
    // Actually TOGGLES, rather than only logging: the claim flow re-reads the account and refuses
    // to spend points unless the line really came back discounted (invariant 2). A mock that logs
    // and changes nothing would fail every claim in the browser — and, worse, a mock that always
    // succeeds would hide the empty-`discounts` case the real device is in today.
    toggleLineDiscount: async (code, lineId) => {
      const line = account.lines?.find((l) => l.lineId === lineId)
      if (!line) throw new Error(`no such line ${lineId}`)
      const on = Array.isArray(line.discounts) && line.discounts.length > 0
      line.discounts = on ? [] : [{ code }]
      log(`${on ? 'removed' : 'applied'} ${code} on line ${lineId}`)
    },
    toggleGlobalDiscount: async (code) => log(`toggled global ${code} on whole account`),
    addExternalReference: (reference, prefix = BADGER_REFERENCE_PREFIX) => {
      account.externalReferences = [...(account.externalReferences ?? []), { prefix, reference }]
      log(`external reference ${prefix}:${reference}`)
    },
    // Really appends a line, so the "add then discount it" path has something to find. The new
    // line's name/price come from the demo menu — the real POS resolves those from its catalogue.
    addItem: (itemId) => {
      // Accepts EITHER id, and the line it creates always reports both — same as a real ticket.
      const menuItem = MOCK_MENU.find((m) => m.internalId === itemId || m.sku === itemId)
      if (!menuItem) {
        log(`ignored add for unknown item ${itemId}`)
        return
      }
      account.lines = [
        ...(account.lines ?? []),
        {
          lineId: `l-added-${(account.lines?.length ?? 0) + 1}`,
          itemId: menuItem.internalId,
          itemSku: menuItem.sku,
          name: menuItem.name,
          priceCents: menuItem.priceCents,
          quantity: 1,
          discounts: [],
        },
      ]
      log(`added item ${itemId} (${menuItem.name})`)
    },
    setConsumer: (consumer) => log(`set consumer ${consumer.identifier}`),
    // A real read/write store, so the reopen-restore path is exercised in dev exactly as on device.
    storeData: (key, value) => {
      store[key] = value
      log(`stored ${key}`)
    },
    loadData: async (key) => store[key] ?? null,
    onCardScan: () => () => {},
    close: () => log('close'),
  }
}

let cached: LightspeedPosApi | null = null

export function getLightspeedApi(): LightspeedPosApi {
  if (!cached) cached = hasHost() ? createHostApi() : createMockApi()
  return cached
}
